import { useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Avatar,
  type InputProps,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  debounce,
  useTheme,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { HandshakeIcon, HouseIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type {
  Property_Transactions_Bool_Exp,
  TransactionSelectFragment,
} from '@graphql/__generated__/graphql';
import {
  RaAutoComplete,
  type RaAutocompleteProps,
} from '@modules/_shared/components/data-grid/RaAutoComplete';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';

export const TRANSACTION_SELECT_FRAGMENT = gql(/* GraphQL */ `
  fragment TransactionSelect on property_transactions {
    id
    ...TransactionCell
    signature_date
    buyer_broker {
      id
      full_name
    }
    seller_broker {
      id
      full_name
    }
  }
`);

export const SEARCH_TRANSACTIONS = gql(/* GraphQL */ `
  query SearchTransactions(
    $q: String!
    $where: property_transactions_bool_exp
  ) {
    search_property_transactions_tsv(
      args: { search_text: $q }
      limit: 10
      order_by: { created_at: desc }
      where: $where
    ) {
      id
      ...TransactionSelect
    }
  }
`);

const GET_DEFAULT_TRANSACTION = gql(/* GraphQL */ `
  query GetDefaultTransaction($id: uuid!) {
    property_transactions_by_pk(id: $id) {
      id
      ...TransactionSelect
    }
  }
`);

interface TransactionSelectProps
  extends Pick<
    RaAutocompleteProps<
      TransactionSelectFragment & { isRestricted?: true },
      false
    >,
    'autoFocus' | 'disabled' | 'sx' | 'InputProps' | 'fullWidth'
  > {
  transactionId?: string;
  onChange?: (transactionId: string | null) => void;
  InputProps?: InputProps;
  filters?: Property_Transactions_Bool_Exp;
}

export const TransactionSelectItem: React.FC<{
  transaction: TransactionSelectFragment;
  props: React.HTMLAttributes<HTMLLIElement>;
  selected?: boolean;
}> = ({ transaction, props }) => {
  const { t, locale } = useLocale();
  const { palette: colors } = useTheme();

  return (
    <ListItem
      {...props}
      key={transaction.id}
      style={{
        padding: '1px 10px',
      }}
    >
      <ListItemIcon>
        <ListItemAvatar>
          <Avatar variant="rounded" sx={{ width: 50, height: 50 }}>
            {transaction.property?.cover_images &&
            transaction.property?.cover_images.length > 0 ? (
              <Image
                src={transaction.property.cover_images[0].image.url ?? ''}
                options={{ w: 256, f: 'jpg' }}
              />
            ) : (
              <HouseIcon
                style={{ margin: 8 }}
                size={32}
                color={colors.grey[600]}
              />
            )}
          </Avatar>
        </ListItemAvatar>
      </ListItemIcon>
      <ListItemText
        sx={theme => theme.text.ellipsis}
        primary={formatAddress(transaction.property, ', ')}
        secondary={[
          transaction.buyer_broker?.full_name != null
            ? t('Buyer: {{name}}', {
                name: transaction.buyer_broker.full_name,
              })
            : null,
          transaction.seller_broker?.full_name != null
            ? t('Seller: {{name}}', {
                name: transaction.seller_broker.full_name,
              })
            : null,
          transaction.signature_date != null
            ? t('Signed on: {{date}}', {
                date: new Intl.DateTimeFormat(locale, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                }).format(new Date(transaction.signature_date)),
              })
            : null,
        ]
          .filter(Boolean)
          .join(', ')}
      />
    </ListItem>
  );
};

export const TransactionSelect = ({
  onChange,
  transactionId,
  autoFocus = false,
  disabled = false,
  sx,
  InputProps,
  filters,
  ...props
}: TransactionSelectProps) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<TransactionSelectFragment[]>([]);
  const prevTransactionId = useRef<string | undefined>(transactionId);
  const [selectedValue, setSelectedValue] = useState<
    (TransactionSelectFragment & { isRestricted?: true }) | null
  >(null);

  const { data: defaultTransaction } = useQuery(GET_DEFAULT_TRANSACTION, {
    skip: transactionId == null,
    variables: { id: transactionId ?? '' },
  });

  useEffect(() => {
    if (defaultTransaction?.property_transactions_by_pk != null) {
      setSelectedValue(defaultTransaction.property_transactions_by_pk);
    } else if (transactionId != null) {
      // We have a transaction but we don't have access to it (permission denied).
      setSelectedValue({
        id: transactionId,
        property: {
          id: transactionId,
          cover_images: [],
        },
        isRestricted: true,
      });
    }
  }, [defaultTransaction, transactionId, t]);

  if (prevTransactionId.current !== transactionId) {
    prevTransactionId.current = transactionId;

    if (transactionId == null) {
      setSelectedValue(null);
    }
  }

  const [searchTransactions, { loading, error }] = useLazyQuery(
    SEARCH_TRANSACTIONS,
    { notifyOnNetworkStatusChange: true },
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        searchTransactions({
          variables: {
            q: value,
            where: filters,
          },
        }).then(response => {
          setOpen(true);
          setOptions(response.data?.search_property_transactions_tsv ?? []);
        });
      }, 275),
    [searchTransactions, filters],
  );

  return (
    <RaAutoComplete<TransactionSelectFragment & { isRestricted?: true }>
      sx={sx}
      InputProps={{
        ...InputProps,
        startAdornment: InputProps?.startAdornment ?? (
          <SelectStartIcon icon={HandshakeIcon} />
        ),
        placeholder: InputProps?.placeholder ?? t('Search transactions'),
      }}
      open={open}
      setOpen={setOpen}
      options={options}
      setOptions={setOptions}
      loading={loading}
      value={selectedValue}
      error={error != null}
      autoFocus={autoFocus}
      debouncedSearch={debouncedSearch}
      onChange={(_event, value) => {
        setSelectedValue(value);
        onChange?.(value?.id ?? null);
      }}
      disabled={disabled || selectedValue?.isRestricted}
      getOptionLabel={option =>
        option.isRestricted
          ? t('Restricted transaction')
          : formatAddress(option.property, ', ')
      }
      tooltip={
        selectedValue?.isRestricted
          ? t("You don't have access to this transaction")
          : undefined
      }
      renderOption={(props, option) => (
        <TransactionSelectItem transaction={option} props={props} />
      )}
      {...props}
    />
  );
};
